import { motion } from 'framer-motion'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import PageHero from '@/components/layout/PageHero'
import { BOARD_OF_DIRECTORS, SENIOR_MANAGEMENT, type TeamMember } from '@/data/leadership'

function ProfileCard({ member, index }: { member: TeamMember; index: number }) {
  const initials = member.name
    .split(' ')
    .map((n) => n[0])
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .join('')

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="group flex overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 hover:border-slate-300 transition-all duration-300 hover:shadow-md"
    >
      <div className="flex-shrink-0 w-40 md:w-52 overflow-hidden bg-slate-200">
        {member.image ? (
          <img
            src={member.image}
            alt={member.name}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full w-full min-h-[160px] items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
            <span className="text-2xl font-bold font-heading text-white/90">
              {initials}
            </span>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 p-5">
        <h3 className="text-lg font-bold font-heading text-primary tracking-tight">
          {member.name}
        </h3>
        <p className="mt-0.5 text-sm font-semibold text-accent">
          {member.title}
        </p>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          {member.bio}
        </p>
      </div>
    </motion.article>
  )
}

export default function Leadership() {
  return (
    <div className="min-h-screen bg-white font-sans text-foreground selection:bg-accent selection:text-white">
      <Navbar light />
      <main className="relative">
        <PageHero
          label="About Us"
          title="Leadership Team"
          intro="Lynas is led by an exceptional team of executives with deep expertise in mining, processing and rare earth materials. Our leadership is committed to sustainable operations and delivering value to shareholders, customers and communities."
        />
        <div className="container max-w-6xl mx-auto px-6 pt-16 pb-24 md:pt-20 md:pb-32">
          <section className="mb-20">
            <h2 className="text-sm font-semibold text-accent uppercase tracking-wider mb-6">
              Board of Directors
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {BOARD_OF_DIRECTORS.map((member, i) => (
                <ProfileCard key={member.name} member={member} index={i} />
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-accent uppercase tracking-wider mb-6">
              Senior Management Team
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {SENIOR_MANAGEMENT.map((member, i) => (
                <ProfileCard key={member.name} member={member} index={i} />
              ))}
            </div>
          </section>

          <p className="mt-20 text-center text-sm text-slate-500">
            For detailed information, please refer to our{' '}
            <a
              href="https://www.asx.com.au/asx/share-price-research/company/LYC"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline font-medium"
            >
              ASX announcements
            </a>{' '}
            and annual report.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  )
}
